/**
 * A saved version, drawn in the document's own pane (RFC 0313 3c).
 *
 * View in the History column used to print the version's source in a box
 * 256px wide, which for an HTML page is a wall of markup: you could not see
 * what the page looked like then. The pane is where the document is read, so
 * the version is read there too, as itself, until you go back to current.
 *
 * Read-only by construction: it renders bytes it was handed and holds no
 * document, so nothing typed or clicked here can write anywhere.
 */

import { MarkdownReader } from './MarkdownReader'

/** What the History column hands the pane when View is pressed. */
export interface VersionPreview {
  /** The version's id, so its marker can say it is the one showing. */
  id: string
  name: string
  /** Epoch ms, as the version stores it. */
  at: number
  content: string
}

export function VersionPreviewPane({
  preview,
  mime,
  projectId,
  documentId,
  onClose,
}: {
  preview: VersionPreview
  mime: string
  projectId: string
  documentId: string
  onClose: () => void
}) {
  const when = new Date(preview.at).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center gap-2 border-border border-b bg-muted/40 px-3 py-1.5 text-xs">
        <span className="text-muted-foreground">⚑</span>
        <span className="min-w-0 flex-1 truncate">
          Viewing <span className="text-foreground">{preview.name}</span>
          <span className="text-muted-foreground"> · saved {when}</span>
        </span>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          Back to current
        </button>
      </div>
      {mime === 'text/html' ? (
        <iframe
          // Keyed on the version: an iframe reads srcDoc once, at load.
          key={preview.id}
          title={`${preview.name} (saved version)`}
          sandbox="allow-scripts"
          srcDoc={preview.content}
          className="h-full w-full flex-1 border-0 bg-white"
        />
      ) : mime === 'text/markdown' ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <MarkdownReader
            documentId={documentId}
            projectId={projectId}
            content={preview.content}
          />
        </div>
      ) : (
        <pre className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap break-words p-4 text-sm">
          {preview.content}
        </pre>
      )}
    </div>
  )
}
